import i from 'i18next'
import useMedia from 'use-media'
import { motion } from 'framer-motion'
import { useContext } from 'react'
import { useRouter } from 'next/dist/client/router'
import { State } from './Layout'
import Link from './Link'
import Logo from './Logo'
import { mobileMenuMaxWidth } from 'constants/styles'
import { headers as ease } from 'constants/easings'
import Flyout from './flyouts/Flyout'
import { MenuLink } from 'lib/hooks/use-collections'
import Menu from './svg/Menu'

export const ALL_PAGES = [
	'/story',
	'/ingredients',
	'/packaging',
	// '/learn',
	'/jobs',
]

type Props = {
	links: MenuLink[]
	loaded: boolean
}

export default function Nav({ links: nav, loaded }: Props ) {
	const { asPath: route } = useRouter()
	const { cart, setFlyout } = useContext( State )
	const mobile = useMedia({ maxWidth: `${mobileMenuMaxWidth}px` })
	const links = nav
		.map( ({ url, title }) => ({
			href: url,
			text: title,
		}) )

	return (
		<motion.div
			style={{ position: 'relative', zIndex: 3 }}
			initial={( loaded ? false : { opacity: 0, y: -70 })}
			animate={{ opacity: 1, y: 0 }}
			transition={{
				default: { duration: 1.5, delay: .2, ease },
				opacity: { duration: 1.5, delay: .6, ease },
			}}
		>
			<nav className="cf top">
				<div>
					<Link href="/">
						<a aria-label="Ray Logo" style={{ paddingRight: 40 }}>
							<Logo />
						</a>
					</Link>
				</div>

				{mobile ? (
					<Mobile links={links} current={route} />
				) : (
					<Main links={links} current={route} />
				)}

				<a
					onClick={() => setFlyout( 'cart' )}
					className="cart"
				>
					<span className="text">{i.t( 'nav.cart.count' )}</span>
					<span className="count" suppressHydrationWarning>
						{cart?.count || 0}
					</span>
				</a>
			</nav>
			{mobile && (
				<Horizontal>
					<Links links={links} current={route} />
				</Horizontal>
			)}
			<style jsx>{`
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					nav.top > ul {
						display: none;
					}
					nav.top .cart {
						display: flex;
						align-items: center;
						width: auto;
						margin-left: auto;
						margin-right: 10px;
						white-space: nowrap;
					}
					nav.top .cart .text {
						display: none;
					}
				}
			`}</style>
		</motion.div>
	)
}

type LinksProps = {
	links: {
		href: string
		text: React.ReactNode
	}[]
	current?: string
}

export const Links = ({ links, current }: LinksProps ) => (
	<motion.ul
		variants={{
			visible: {
				transition: { staggerChildren: .06 },
			},
		}}
	>
		{links.map( ({ href, text }) => (
			<motion.li
				key={href}
				variants={{
					hidden: { opacity: 0, y: 50 },
					visible: { opacity: 1, y: 0 },
				}}
				transition={{
					opacity: { duration: 1, ease },
					default: { duration: 1, ease },
				}}
			>
				<Link href={href}>
					<a>{text}</a>
				</Link>
				{current?.startsWith( href ) && (
					<motion.div
						// layoutId="nav-underline"
						className="underline"
					/>
				)}
			</motion.li>
		) )}
	</motion.ul>
)

type BlockProps = Pick<LinksProps, 'current'|'links'>

export const Main = ({ links, current }: BlockProps ) => (
	<Links links={links} current={current} />
)

export const Mobile = ({ links, current }: BlockProps ) => {
	const { flyout, setFlyout } = useContext( State )
	const open = flyout === 'menu' || flyout === 'collections'
	const onClose = () => setFlyout( false )

	return (
		<div>
			<a className="control" onClick={() => setFlyout( 'menu' )}>
				<Menu />
			</a>
			<Flyout active={open} onClose={onClose}>
				<Main links={links} current={current} />
			</Flyout>
			<style jsx>{`
				div {
					order: 2;
				}
				a {
					cursor: pointer;
				}
				:global( body ) {
					overflow: ${( open ? 'hidden' : 'auto' )};
				}
				div :global( ul ) {
					display: block;
				}
				div :global( ul li ) {
					margin: 1em 0;
					font-size: 20px;
					line-height: 1.36;
					width: fit-content;
				}
				div :global( ul a[href="/products"]::after ) {
					content: "";
					display: inline-block;
					width: 26px;
					height: 12px;
					margin-left: 20px;
					background-image: url( "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNiAxMiIgd2lkdGg9IjI2IiBoZWlnaHQ9IjEyIj48cG9seWdvbiBmaWxsPSIjMDEwMjAyIiBwb2ludHM9IjE4LjQsMS40IDIyLjEsNSAwLDUgMCw3IDIyLjEsNyAxOC40LDEwLjYgMTkuOSwxMiAyNiw2IDE5LjgsMCIgLz48L3N2Zz4=" );
				}
			`}</style>
		</div>
	)
}

const Horizontal = ({ children }) => (
	<nav className="cf top">
		{children}
		<style jsx>{`
			nav.top {
				height: 40px;
				overflow-x: auto;
				-ms-overflow-style: none;
				scrollbar-width: none;
				transform: translateY(-30px);
			}
			nav.top::-webkit-scrollbar {
				display: none;
			}

			nav :global( ul ) {
				margin-inline: auto;
				gap: clamp(20px, 5vw, 40px);
				justify-content: space-between;
			}
			nav :global( li ) {
				margin-inline: 0;
			}
		`}</style>
	</nav>
)
